import {
  createContext,
  useContext,
  useReducer,
  type Dispatch,
  type ReactNode,
} from "react";
import type {
  ApiError,
  SearchPreferences,
  SearchResponse,
} from "../types";

interface State {
  artist: string;
  preferences: SearchPreferences;
  results: SearchResponse | null;
  loading: boolean;
  error: ApiError | null;
}

type Action =
  | { type: "SET_ARTIST"; artist: string }
  | { type: "SET_PREFERENCES"; preferences: Partial<SearchPreferences> }
  | { type: "SET_RESULTS"; results: SearchResponse | null }
  | { type: "SET_LOADING"; loading: boolean }
  | { type: "SET_ERROR"; error: ApiError | null };

const defaultPreferences: SearchPreferences = {
  origin_airport: "",
  flexibility_days: 3,
  nonstop_only: false,
  max_stops: 1,
  hotel_stars: 3,
};

const initialState: State = {
  artist: "",
  preferences: defaultPreferences,
  results: null,
  loading: false,
  error: null,
};

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case "SET_ARTIST":
      return { ...state, artist: action.artist };
    case "SET_PREFERENCES":
      return {
        ...state,
        preferences: { ...state.preferences, ...action.preferences },
      };
    case "SET_RESULTS":
      return { ...state, results: action.results };
    case "SET_LOADING":
      return { ...state, loading: action.loading };
    case "SET_ERROR":
      return { ...state, error: action.error };
    default:
      return state;
  }
}

interface ContextValue {
  state: State;
  dispatch: Dispatch<Action>;
}

const SearchContext = createContext<ContextValue | undefined>(undefined);

export function SearchProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(reducer, initialState);
  return (
    <SearchContext.Provider value={{ state, dispatch }}>
      {children}
    </SearchContext.Provider>
  );
}

export function useSearchContext(): ContextValue {
  const ctx = useContext(SearchContext);
  if (!ctx) {
    throw new Error("useSearchContext must be used within a SearchProvider");
  }
  return ctx;
}
